<template>
  <div class="page-wrapper">
    <div class="client-box hub-grey">
      <div class="list-top">
        <h1>Client List{{ loginUser.team.name ? ` - ${loginUser.team.name}` : '' }}</h1>
        <div class="header-buttons">
          <button
            type="button"
            class="icon-btn add-btn shaded-btn hub-link"
            @click="$router.push({ name: 'newClient' })"
          >
            <inline-svg :src="$assets.svg['add-client-1']" height="100%" />
            <span>New Client</span>
          </button>
          <button type="button" class="reset-all-btn shaded-btn hub-link" @click="showResetConfirmation = true">
            Reset All Client Passwords
          </button>
        </div>
      </div>
    </div>
    <ul class="client-list">
      <li class="list-head">
        <div class="col-avatar">Avatar</div>
        <div class="col-id">Identification</div>
        <div class="col-fac">Registered by</div>
        <div class="col-stat">Status</div>
        <div class="col-profile">Profile</div>
      </li>

      <li :class="{ 'list-row': true, closed: client.closeTime }" v-for="client in clientList" :key="client.id">
        <div class="col-avatar">
          <div class="svg-box" :style="{ backgroundColor: client.avatarColor }">
            <inline-svg :src="$assets.svg.avatar[client.avatarKey]" height="100%" fill="#FFF" />
          </div>
        </div>

        <div class="col-id">ID: {{ client.code }}</div>
        <div class="col-fac"><span>FAC: </span>{{ client.fFirstName + ' ' + client.fLastName }}</div>

        <div class="col-stat">
          <span>Status: </span>
          {{ $filters.clientStatus(client.status) }}
          <template v-html="client.closeTime ? `<br />${$filters.timeToNow(client.closeTime)}` : '==='"></template>
        </div>
        <div class="col-profile">
          <div
            class="profile-btn clickable"
            @click="showClientProfile(client.id)"
            @keypress.enter="showClientProfile(client.id)"
          >
            Profile
          </div>
        </div>
      </li>
    </ul>

    <ClientModal v-if="showResetConfirmation" width="521px" height="258px" @close="showResetConfirmation = false">
      <h1 class="modal-header">Password Reset For All Clients</h1>
      <p class="modal-description">
        You are about to reset all your clients’ passwords. To continue, please input your password and confirm with the
        “Reset Password” button below.
      </p>
      <PasswordConfirmationField v-model="checkPassword" @confirmed="resetAllPasswords" />
      <button type="button" class="confirm-button" @click="checkPassword = true">RESET ALL PASSWORDS</button>
    </ClientModal>

    <ClientPasswordPopup
      v-if="showNewPasswords"
      :title="newPasswordInfo.title"
      :description="newPasswordInfo.description"
      :showCSVDownload="true"
      :passwordClientList="passwordClientList"
      @close="closePasswordList"
    />

    <GlobalLoading v-if="isLoading" />
  </div>
</template>

<script setup>
import { ref, onBeforeMount, computed } from 'vue';
import { useRouter } from 'vue-router';

import $assets from '@/assets';
import ClientModal from '@/components/client/ClientModal.vue';
import ClientPasswordPopup from '@/components/client/ClientPasswordPopup.vue';
import PasswordConfirmationField from '@/components/client/PasswordConfirmationField.vue';
import GlobalLoading from '@/components/common/GlobalLoading.vue';
import ClientService from '@/services/client';
import useSessionStore from '@/stores/session';

const $router = useRouter();
const SessionStore = useSessionStore();
const clientList = ref([]);
const passwordClientList = ref([]);

const newPasswordInfo = ref({
  title: 'Password Reset For All Clients',
  description:
    "All your clients' Passwords have been reset. Please take a moment to copy the passwords to a safe location. The password will be encrypted once the client profile is registered. For security purposes, our system will not be able to decrypt the password for you.",
});

const showResetConfirmation = ref(false);
const checkPassword = ref(false);
const showNewPasswords = ref(false);
const isLoading = ref(false);

const loginUser = computed(() => {
  return SessionStore.loginUser;
});

onBeforeMount(async () => {
  const clientResults = await ClientService.getClients(loginUser.value.team.id);
  clientList.value = clientResults.records.map((client) => {
    return {
      id: client.id,
      code: client.code,
      closeTime: client.close_time,
      avatarColor: client.avatar_color_meta_value,
      avatarKey: client.avatar_meta_value,
      status: client.status_meta_value,
      fFirstName: client.facilitator_first_name,
      fLastName: client.facilitator_last_name,
    };
  });
});

function showClientProfile(clientId) {
  $router.push({ path: `/client/${clientId}` });
}

async function resetAllPasswords() {
  showResetConfirmation.value = false;
  isLoading.value = true;
  const passwordClientResults = await ClientService.resetTeamPasswords(loginUser.value.team.id);
  passwordClientList.value = passwordClientResults.records;
  isLoading.value = false;
  showNewPasswords.value = true;
}
function closePasswordList() {
  passwordClientList.value = [];
  showNewPasswords.value = false;
}
</script>

<style scoped>
.page-wrapper {
  width: 100%;
  height: 100%;
}

.client-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  padding: 3em 0;
}

.list-top {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}
.list-top h1 {
  text-align: center;
}

.header-buttons {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}
.shaded-btn {
  height: 2.8em;
  padding: 0 1em;

  font-size: 1em;

  color: #808285;
  background-color: white;

  border: none;
  border-radius: 0.6em;
}
.reset-all-btn {
  filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25));
}
.icon-btn {
  display: flex;
  justify-content: center;
  align-items: center;
}
.add-btn {
  margin-right: 0.5em;

  filter: drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.25));
}
.icon-btn svg {
  height: 27px;
  fill: #808285;
}
.icon-btn > span {
  white-space: nowrap;
  margin-left: 0.5em;
}
.client-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, 90%);
  justify-content: space-evenly;

  margin-top: 3em;
  width: 100%;
}
.list-head {
  display: none;
}

.list-row {
  display: grid;
  justify-content: space-between;
  align-items: center;
  grid-template-areas:
    'a d'
    'b d'
    'c d'
    'e e';

  padding: 1em 0.5em;
  border-bottom: 1px solid #a7a9ac;
}
.list-row:nth-child(2) {
  border-top: 1px solid #a7a9ac;
}

.col-avatar {
  display: flex;
  justify-content: center;
  grid-area: d;
}
.col-avatar .svg-box {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 3em;
  height: 3em;
  padding: 1em;
}
.col-profile {
  grid-area: e;
  padding-top: 1em;
}
.col-profile .profile-btn {
  width: 50%;
  border-radius: 0.4em;
  padding: 0.5em 0;
  background: #8ac640;
  color: #fff;
  text-align: center;
}
.col-profile .profile-btn:hover {
  box-shadow: 0 0 10px 0 #a7a9ac;
}

.closed .col-avatar {
  opacity: 0.4;
}
.closed .col-id,
.closed .col-fac {
  color: #a7a9ac;
}
.closed .profile-btn {
  background-color: #6d6e71;
  color: #ffffff;
}

.list-head > div,
.list-row > div {
  white-space: pre-wrap;
  word-break: break-all;
}

.modal .confirm-button {
  height: 42px;
  width: 210px;

  background: #62a517;
  border: none;
  border-radius: 9px;

  color: #ffffff;
  text-align: center;

  font-family: 'LatoRegular';
  font-weight: 700;
  font-size: 15px;

  cursor: pointer;
}

/** Landscape mobile */
@media only screen and (orientation: landscape) and (max-height: 600px) {
  .list-head {
    display: grid;
    grid-template-areas: 'd a b c e';
    grid-template-columns: 15% 20% 25% 18% 1fr;
    justify-content: space-between;
    align-items: center;

    padding: 0 0.5em 0.5em;
    border-bottom: 1px solid #a7a9ac;
  }
  .list-row {
    grid-template-areas: 'd a b c e';
    grid-template-columns: 15% 20% 25% 18% 1fr;
  }
  .list-row:nth-child(2) {
    border-top: 0 none;
  }

  .col-avatar .svg-box {
    width: 2.4em;
    height: 2.4em;
    padding: 0.8em;
  }

  .col-avatar {
    margin-right: 30%;
  }

  .col-profile {
    padding: 0;
  }
  .col-profile .profile-btn {
    width: 100%;
  }

  .list-row > div > span {
    display: none;
  }
}

/** Larger devices */
@media only screen and (min-width: 601px) and (min-height: 601px) {
  .list-top {
    flex-direction: row;
    justify-content: space-between;

    margin: 0 auto;
    width: 90%;
  }
  .list-head {
    display: grid;
    grid-template-areas: 'd a b c e';
    grid-template-columns: 15% 20% 25% 18% 1fr;
    justify-content: space-between;
    align-items: center;

    padding: 0 0.5em 0.5em;
    border-bottom: 1px solid #a7a9ac;
  }
  .list-row {
    grid-template-areas: 'd a b c e';
    grid-template-columns: 15% 20% 25% 18% 1fr;
  }
  .list-row:nth-child(2) {
    border-top: 0 none;
  }

  .col-avatar {
    margin-right: 30%;
  }

  .col-profile {
    padding: 0;
  }
  .col-profile .profile-btn {
    max-width: 8em;
    width: 100%;
  }

  .list-row > div > span {
    display: none;
  }
}
</style>
